import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import api from '../services/api';
import { getSoundEnabled, getEscalationSound, audioDeTono, SOUND_PREF_EVENT, SOUND_STORAGE_KEYS } from '../utils/soundPrefs';
import { SocketContext } from '../context/SocketContext';

const BadgesContext = createContext();

export const useBadges = () => useContext(BadgesContext);

// Sonidos de notificación: archivos .ogg en /public/sounds/
const messageAudio = typeof Audio !== 'undefined' ? new Audio('/sounds/message.ogg') : null;
if (messageAudio) messageAudio.preload = 'auto';
// Escalaciones: el tono lo elige cada usuario; se resuelve AL SONAR para que un
// cambio de preferencia valga sin recargar.
const escalationAudio = () => audioDeTono(getEscalationSound().id);
escalationAudio(); // precarga del tono vigente

const playSound = (audio) => {
  if (!audio) return;
  try {
    audio.currentTime = 0;
    const p = audio.play();
    if (p && typeof p.catch === 'function') {
      p.catch((e) => console.log('[BADGES] Audio play blocked:', e?.message || e));
    }
  } catch (e) {
    console.log('[BADGES] Could not play sound:', e);
  }
};

// App Badge API - muestra contador en el ícono de la PWA
const updateAppBadge = async (count) => {
  if ('setAppBadge' in navigator) {
    try {
      if (count > 0) {
        await navigator.setAppBadge(count);
        console.log('[BADGES] App badge set to:', count);
      } else {
        await navigator.clearAppBadge();
        console.log('[BADGES] App badge cleared');
      }
    } catch (err) {
      console.warn('[BADGES] App Badge API error:', err);
    }
  }
};

export const BadgesProvider = ({ children }) => {
  const [badges, setBadges] = useState({
    escalations: 0,
    tickets: 0,
    unassigned: 0,
    dev_issues: 0,
    clientes_riesgo: 0,
    conversations_unread: 0,
    seguimientos: 0,
    calendario: 0
  });
  const [flash, setFlash] = useState({});
  // Flag por-usuario (admin_users.sound_enabled); el espejo local lo sincroniza AuthContext
  const [soundEnabled, setSoundEnabledState] = useState(getSoundEnabled());
  // Un solo socket para todo el CRM: se reusa la conexión de SocketContext en
  // vez de abrir una segunda (antes BadgesProvider tenía su propio io(...) —
  // dos WebSockets/reconexiones/heartbeats en paralelo para lo mismo).
  const { socket, connected } = useContext(SocketContext);
  const prevBadges = useRef(badges);
  const isTabVisible = useRef(true);
  const soundEnabledRef = useRef(getSoundEnabled());
  const escalationsRef = useRef(0);
  const lastEscalationSoundAt = useRef(0);

  // Track tab visibility — ya NO limpiamos el app badge cuando la tab es visible
  // para que siempre refleje los items importantes (escalations + unread)
  useEffect(() => {
    const handleVisibilityChange = () => {
      isTabVisible.current = !document.hidden;
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  // Fetch inicial de badges
  const fetchBadges = useCallback(async () => {
    try {
      // /api/crm/badges usa la MISMA fuente que los pushes del socket
      // (realtime_badges.get_badge_counts) → keys consistentes siempre
      const res = await api.get('/api/crm/badges');
      if (res.data) {
        prevBadges.current = res.data;
        escalationsRef.current = res.data.escalations || 0;
        setBadges(res.data);
        // El app badge se actualiza por el useEffect que observa `badges`
      }
    } catch (err) {
      console.error('[BADGES] Error fetching initial badges:', err);
    }
  }, []);

  // Single source of truth para el app badge de la PWA: refleja la cola de
  // escalaciones pendientes (la API del SO sólo admite UN número). Las
  // conversaciones sin leer ya tienen su propio badge en el sidebar.
  // Se limpia cuando escalations llega a 0.
  useEffect(() => {
    updateAppBadge(badges.escalations || 0);
  }, [badges.escalations]);

  useEffect(() => {
    // Fetch inicial
    fetchBadges();

    // Restaurar sólo el título de la tab cuando el usuario vuelve a enfocar;
    // el app badge de la PWA se mantiene.
    const handleFocus = () => {
      document.title = 'POSBerry CRM';
    };
    window.addEventListener('focus', handleFocus);

    // Un solo timer de 60s para las dos tareas periódicas (antes eran 2
    // setInterval separados con el mismo período):
    // - fallback si el socket falla, y además mantiene fresco el badge de
    //   Calendario (baja solo cuando una reunión termina, sin evento propio)
    // - recordatorio: si hay al menos una escalación pendiente, sonar cada
    //   60s (uno solo, no por cada escalación). Evita doble-sonido si recién
    //   acaba de sonar por un badges_update fresco.
    const tick = setInterval(() => {
      fetchBadges();

      if (soundEnabledRef.current && escalationsRef.current > 0) {
        const since = Date.now() - lastEscalationSoundAt.current;
        if (since >= 55000) {
          playSound(escalationAudio());
          lastEscalationSoundAt.current = Date.now();
        }
      }
    }, 60000);

    return () => {
      clearInterval(tick);
      window.removeEventListener('focus', handleFocus);
    };
  }, [fetchBadges]);

  // Escuchar badges_update en el socket COMPARTIDO de SocketContext (no se
  // crea conexión propia). Se re-suscribe si la instancia de socket cambia
  // (p.ej. login/logout, que recrea el socket en SocketProvider).
  useEffect(() => {
    if (!socket) return undefined;

    const handleBadgesUpdate = (data) => {
      console.log('[BADGES] Update received:', data);
      const newFlash = {};
      let hasNewItems = false;

      Object.keys(data).forEach(key => {
        if (data[key] > (prevBadges.current[key] || 0)) {
          newFlash[key] = true;
          hasNewItems = true;
        }
      });

      if (Object.keys(newFlash).length > 0) {
        setFlash(newFlash);
        setTimeout(() => setFlash({}), 2000);
      }

      // Sonidos diferenciados por tipo de evento
      if (soundEnabledRef.current) {
        // Escalación: suena siempre que sube (tab visible o no)
        if (newFlash.escalations) {
          playSound(escalationAudio());
          lastEscalationSoundAt.current = Date.now();
        }
        // Mensajes nuevos: solo cuando la tab está inactiva
        if (newFlash.conversations_unread && !isTabVisible.current) {
          playSound(messageAudio);
        }
      }

      // Actualizar título cuando la tab está inactiva
      if (hasNewItems && !isTabVisible.current) {
        const total = Object.values(data).reduce((a, b) => a + b, 0);
        if (total > 0) {
          document.title = '(' + total + ') POSBerry CRM';
        }
      }

      escalationsRef.current = data.escalations || 0;
      prevBadges.current = data;
      setBadges(data);
      // El app badge lo actualiza el useEffect que observa `badges` — acá no
      // hace falta. Antes se borraba al enfocar la tab; ahora persiste mientras
      // haya escalaciones o unread.
    };

    socket.on('badges_update', handleBadgesUpdate);

    return () => {
      socket.off('badges_update', handleBadgesUpdate);
    };
  }, [socket]);

  // Marcar como leído: resetea el título de la tab. El app badge no se toca
  // acá — sigue la fuente de verdad (escalations + unread). Se limpia sólo
  // cuando esos contadores llegan a 0.
  const markAsRead = useCallback(() => {
    document.title = 'POSBerry CRM';
  }, []);

  // Mantener refs sincronizados
  useEffect(() => {
    soundEnabledRef.current = soundEnabled;
  }, [soundEnabled]);

  // Sincronizar entre pestañas / cambios desde otro componente.
  useEffect(() => {
    const onPref = () => setSoundEnabledState(getSoundEnabled());
    const onStorage = (e) => {
      if (SOUND_STORAGE_KEYS.includes(e.key)) setSoundEnabledState(getSoundEnabled());
    };
    window.addEventListener(SOUND_PREF_EVENT, onPref);
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener(SOUND_PREF_EVENT, onPref);
      window.removeEventListener('storage', onStorage);
    };
  }, []);

  return (
    <BadgesContext.Provider value={{
      badges,
      flash,
      connected,
      socket,
      refetch: fetchBadges,
      soundEnabled,
      markAsRead
    }}>
      {children}
    </BadgesContext.Provider>
  );
};

export default BadgesContext;
